/**
 * A6 删除回收 / 客服代恢复页
 *
 * Stream 2 Phase 2-5b / Plan §10 Stream 2 / Design §5.3 A6.
 *
 * 单页 + 3 Tab：
 *   Tab 1 — 客服代恢复（RestoreJobsTab）：发起 / 审批 / 拒绝 / 执行
 *   Tab 2 — 清理监控（LifecycleJobsTab）：只读监控
 *   Tab 3 — 强制清理（ForcePurgeTab）：兜底强制清理 + 司法清理（严格限定）
 *
 * 权限：
 *   - 整页 PermissionGate USER_READ (fallback 403)
 *   - Tab 1 操作 PermissionGate USER_RESTORE
 *   - Tab 3 兜底 PermissionGate USER_FREEZE
 *   - Tab 3 司法 PermissionGate LIFECYCLE_FORCE_PURGE_JUDICIAL
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Result,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Drawer,
  Descriptions,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { PERMISSIONS } from '../../contracts/permissions';
import {
  listRestoreJobs,
  getRestoreJob,
  initiateRestoreJob,
  approveRestoreJob,
  rejectRestoreJob,
  executeRestoreJob,
  listLifecycleJobs,
  triggerForcePurge,
  triggerJudicialPurge,
  type RestoreJobItem,
  type RestoreJobDetail,
  type RestoreJobStatus,
  type LifecycleJobItem,
  type LifecycleJobType,
  type LifecycleJobStatus,
  type ListRestoreJobsFilter,
  type ListLifecycleJobsFilter,
} from '../../api/lifecycle';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { TextArea } = Input;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function restoreStatusColor(status: RestoreJobStatus): string {
  switch (status) {
    case 'pending_approval': return 'blue';
    case 'approved':         return 'gold';
    case 'rejected':         return 'red';
    case 'executed':         return 'green';
    case 'failed':           return 'purple';
    default:                 return 'default';
  }
}

function restoreStatusLabel(status: RestoreJobStatus): string {
  switch (status) {
    case 'pending_approval': return '待审批';
    case 'approved':         return '已审批';
    case 'rejected':         return '已拒绝';
    case 'executed':         return '已执行';
    case 'failed':           return '执行失败';
    default:                 return status;
  }
}

function lifecycleJobTypeLabel(t: LifecycleJobType): string {
  switch (t) {
    case 'force_purge_recycled': return '兜底强制清理';
    case 'judicial_purge':       return '司法清理';
    case 'lifecycle_recycle':    return '进入回收';
    case 'lifecycle_purge':      return '自动清理';
    default:                     return t;
  }
}

function lifecycleJobTypeColor(t: LifecycleJobType): string {
  switch (t) {
    case 'force_purge_recycled': return 'red';
    case 'judicial_purge':       return 'magenta';
    case 'lifecycle_recycle':    return 'orange';
    case 'lifecycle_purge':      return 'volcano';
    default:                     return 'default';
  }
}

function lifecycleStatusColor(s: LifecycleJobStatus): string {
  switch (s) {
    case 'pending':   return 'blue';
    case 'running':   return 'gold';
    case 'succeeded': return 'green';
    case 'failed':    return 'red';
    default:          return 'default';
  }
}

function lifecycleStatusLabel(s: LifecycleJobStatus): string {
  switch (s) {
    case 'pending':   return '待处理';
    case 'running':   return '处理中';
    case 'succeeded': return '已完成';
    case 'failed':    return '失败';
    default:          return s;
  }
}

const RESTORE_STATUS_OPTIONS = [
  { value: 'pending_approval', label: '待审批' },
  { value: 'approved', label: '已审批' },
  { value: 'rejected', label: '已拒绝' },
  { value: 'executed', label: '已执行' },
  { value: 'failed', label: '执行失败' },
];

const LIFECYCLE_JOB_TYPE_OPTIONS = [
  { value: 'all', label: '全部' },
  { value: 'force_purge_recycled', label: '兜底强制清理' },
  { value: 'judicial_purge', label: '司法清理' },
  { value: 'lifecycle_recycle', label: '进入回收' },
  { value: 'lifecycle_purge', label: '自动清理' },
];

// ---------------------------------------------------------------------------
// Tab 1: RestoreJobs — Modals
// ---------------------------------------------------------------------------

interface InitiateRestoreJobModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function InitiateRestoreJobModal({ open, onClose, onSuccess }: InitiateRestoreJobModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return initiateRestoreJob({
        workId: v.workId,
        userId: v.userId,
        userRequestText: v.userRequestText,
      });
    },
    onSuccess: () => {
      message.success('代恢复申请已发起');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '发起代恢复失败');
    },
  });

  return (
    <Modal
      title="发起客服代恢复"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => {
        form.validateFields().then(() => mutation.mutate()).catch(() => undefined);
      }}
      confirmLoading={mutation.isPending}
      okText="提交申请"
      data-testid="initiate-restore-modal"
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="操作说明"
        description="必须粘贴用户原始请求文字（来自飞书/微信/电话沟通记录），用于 audit 留痕。仅限 work 在回收期内（recycle_expire_at > now）"
        data-testid="initiate-restore-info-alert"
      />
      <Form form={form} layout="vertical">
        <Form.Item
          name="workId"
          label="作品 ID"
          rules={[{ required: true, message: '请输入作品 ID' }]}
        >
          <Input placeholder="workId" />
        </Form.Item>
        <Form.Item
          name="userId"
          label="用户 ID"
          rules={[{ required: true, message: '请输入用户 ID' }]}
        >
          <Input placeholder="userId" />
        </Form.Item>
        <Form.Item
          name="userRequestText"
          label="用户原始请求文字"
          rules={[
            { required: true, message: '请输入用户原始请求文字' },
            { min: 20, message: '请求文字不得少于 20 个字符' },
            { max: 1000, message: '请求文字不得超过 1000 个字符' },
          ]}
        >
          <TextArea
            rows={5}
            placeholder="粘贴用户原始请求文字（20-1000 字符）"
            showCount
            maxLength={1000}
            data-testid="user-request-text-area"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

interface ApproveRestoreModalProps {
  open: boolean;
  jobId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function ApproveRestoreJobModal({ open, jobId, onClose, onSuccess }: ApproveRestoreModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return approveRestoreJob(jobId!, { approvalNote: v.approvalNote || undefined });
    },
    onSuccess: () => {
      message.success('代恢复已审批通过');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '审批失败');
    },
  });

  return (
    <Modal
      title="审批通过"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => mutation.mutate()}
      confirmLoading={mutation.isPending}
      okText="确认通过"
      data-testid="approve-restore-modal"
    >
      <Form form={form} layout="vertical">
        <Form.Item name="approvalNote" label="审批备注（可选）">
          <TextArea rows={3} placeholder="对执行人的说明（可不填）" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

interface RejectRestoreModalProps {
  open: boolean;
  jobId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function RejectRestoreJobModal({ open, jobId, onClose, onSuccess }: RejectRestoreModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return rejectRestoreJob(jobId!, { rejectReason: v.rejectReason });
    },
    onSuccess: () => {
      message.success('代恢复申请已拒绝');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '拒绝操作失败');
    },
  });

  return (
    <Modal
      title="拒绝代恢复"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => {
        form.validateFields().then(() => mutation.mutate()).catch(() => undefined);
      }}
      confirmLoading={mutation.isPending}
      okText="确认拒绝"
      okButtonProps={{ danger: true }}
      data-testid="reject-restore-modal"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="rejectReason"
          label="拒绝原因"
          rules={[
            { required: true, message: '请输入拒绝原因' },
            { min: 5, message: '拒绝原因不得少于 5 个字符' },
          ]}
        >
          <TextArea rows={3} placeholder="请详细说明拒绝原因（不少于 5 字）" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

interface ExecuteRestoreModalProps {
  open: boolean;
  jobId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function ExecuteRestoreJobModal({ open, jobId, onClose, onSuccess }: ExecuteRestoreModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      return executeRestoreJob(jobId!, { executionNote: v.executionNote || undefined });
    },
    onSuccess: () => {
      message.success('代恢复已执行，用户将收到通知');
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '执行代恢复失败');
    },
  });

  return (
    <Modal
      title="执行代恢复"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => mutation.mutate()}
      confirmLoading={mutation.isPending}
      okText="确认执行"
      okButtonProps={{ type: 'primary', danger: true }}
      data-testid="execute-restore-modal"
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="操作确认"
        description="将通过 admin 内部 API 调用 works.restore_by_admin，作品 lifecycle_status 回到 active，并 notify 用户"
        data-testid="execute-restore-warning-alert"
      />
      <Form form={form} layout="vertical">
        <Form.Item name="executionNote" label="执行备注（可选）">
          <TextArea rows={2} placeholder="执行说明（可不填）" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Tab 1: RestoreJobs — Detail Drawer
// ---------------------------------------------------------------------------

interface RestoreDetailDrawerProps {
  open: boolean;
  jobId: string | null;
  onClose: () => void;
}

function RestoreJobDetailDrawer({ open, jobId, onClose }: RestoreDetailDrawerProps) {
  const query = useQuery({
    queryKey: ['restore-job-detail', jobId],
    queryFn: () => getRestoreJob(jobId!),
    enabled: open && jobId != null,
  });

  const d: RestoreJobDetail | undefined = query.data;

  return (
    <Drawer
      title="代恢复任务详情"
      open={open}
      onClose={onClose}
      width={640}
      data-testid="restore-job-detail-drawer"
    >
      {query.isLoading && <Text>加载中…</Text>}
      {d && (
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="任务 ID">{d.id}</Descriptions.Item>
          <Descriptions.Item label="作品 ID">{d.workId}</Descriptions.Item>
          <Descriptions.Item label="用户 ID">{d.userId}</Descriptions.Item>
          <Descriptions.Item label="用户原始请求文字" style={{ whiteSpace: 'pre-wrap' }}>
            {d.userRequestText}
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={restoreStatusColor(d.status)}>{restoreStatusLabel(d.status)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="发起人">{d.initiatorId}</Descriptions.Item>
          <Descriptions.Item label="发起时间">{formatDateTime(d.initiatedAt)}</Descriptions.Item>
          <Descriptions.Item label="审批人">{d.approverId ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="审批时间">
            {d.approvedAt ? formatDateTime(d.approvedAt) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="审批备注">{d.approvalNote ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="拒绝原因">{d.rejectReason ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="执行人">{d.executorId ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="执行时间">
            {d.executedAt ? formatDateTime(d.executedAt) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="执行备注">{d.executionNote ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="执行失败原因">
            {d.executionFailureReason ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">{formatDateTime(d.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{formatDateTime(d.updatedAt)}</Descriptions.Item>
        </Descriptions>
      )}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Tab 1: RestoreJobsTab
// ---------------------------------------------------------------------------

interface RestoreFilterFormValues {
  status?: RestoreJobStatus;
  initiatorId?: string;
  workId?: string;
  userId?: string;
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
}

function RestoreJobsTab() {
  const qc = useQueryClient();
  const [filterForm] = Form.useForm<RestoreFilterFormValues>();
  const [filter, setFilter] = useState<ListRestoreJobsFilter>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [initiateOpen, setInitiateOpen] = useState(false);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [executeId, setExecuteId] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['restore-jobs', filter, page, pageSize],
    queryFn: () => listRestoreJobs({ ...filter, page, pageSize }),
  });

  function applyFilters() {
    const v = filterForm.getFieldsValue();
    const next: ListRestoreJobsFilter = {};
    if (v.status) next.status = v.status;
    if (v.initiatorId?.trim()) next.initiatorId = v.initiatorId.trim();
    if (v.workId?.trim()) next.workId = v.workId.trim();
    if (v.userId?.trim()) next.userId = v.userId.trim();
    if (v.dateRange?.[0]) next.fromDate = v.dateRange[0].toISOString();
    if (v.dateRange?.[1]) next.toDate = v.dateRange[1].toISOString();
    setFilter(next);
    setPage(1);
  }

  function resetFilters() {
    filterForm.resetFields();
    setFilter({});
    setPage(1);
  }

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['restore-jobs'] });
  }

  const columns: ColumnsType<RestoreJobItem> = [
    {
      title: '任务 ID',
      dataIndex: 'id',
      key: 'id',
      width: 160,
      render: (id: string) => (
        <Tooltip title={id}>
          <Text ellipsis style={{ maxWidth: 140, display: 'block' }}>
            {id}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '作品 ID',
      dataIndex: 'workId',
      key: 'workId',
      width: 140,
      ellipsis: true,
    },
    {
      title: '用户 ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 140,
      ellipsis: true,
    },
    {
      title: '用户请求摘要',
      dataIndex: 'userRequestText',
      key: 'userRequestText',
      width: 200,
      render: (t: string) => (
        <Tooltip title={t}>
          <Text ellipsis style={{ maxWidth: 180, display: 'block' }}>
            {t}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: RestoreJobStatus) => (
        <Tag color={restoreStatusColor(s)}>{restoreStatusLabel(s)}</Tag>
      ),
    },
    {
      title: '发起人',
      dataIndex: 'initiatorId',
      key: 'initiatorId',
      width: 130,
      ellipsis: true,
    },
    {
      title: '审批人',
      dataIndex: 'approverId',
      key: 'approverId',
      width: 130,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '执行人',
      dataIndex: 'executorId',
      key: 'executorId',
      width: 130,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: '操作',
      key: 'actions',
      width: 220,
      fixed: 'right' as const,
      render: (_: unknown, row: RestoreJobItem) => (
        <Space size="small">
          {row.status === 'pending_approval' && (
            <PermissionGate point={PERMISSIONS.USER_RESTORE}>
              <>
                <Button
                  size="small"
                  type="primary"
                  onClick={() => setApproveId(row.id)}
                  data-testid={`restore-approve-btn-${row.id}`}
                >
                  审批通过
                </Button>
                <Button
                  size="small"
                  danger
                  onClick={() => setRejectId(row.id)}
                  data-testid={`restore-reject-btn-${row.id}`}
                >
                  拒绝
                </Button>
              </>
            </PermissionGate>
          )}
          {row.status === 'approved' && (
            <PermissionGate point={PERMISSIONS.USER_RESTORE}>
              <Button
                size="small"
                type="primary"
                danger
                onClick={() => setExecuteId(row.id)}
                data-testid={`restore-execute-btn-${row.id}`}
              >
                执行恢复
              </Button>
            </PermissionGate>
          )}
          <Button
            size="small"
            type="link"
            onClick={() => setDetailId(row.id)}
            data-testid={`restore-detail-btn-${row.id}`}
          >
            查看详情
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div data-testid="restore-jobs-tab">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <PermissionGate point={PERMISSIONS.USER_RESTORE}>
          <Button
            type="primary"
            onClick={() => setInitiateOpen(true)}
            data-testid="initiate-restore-btn"
          >
            发起代恢复
          </Button>
        </PermissionGate>

        <Form
          form={filterForm}
          layout="inline"
          style={{ flex: 1, flexWrap: 'wrap', gap: 8 }}
          data-testid="restore-jobs-filter-form"
        >
          <Form.Item name="status" label="状态">
            <Select
              placeholder="全部"
              allowClear
              style={{ width: 120 }}
              options={RESTORE_STATUS_OPTIONS}
            />
          </Form.Item>
          <Form.Item name="initiatorId" label="发起人 ID">
            <Input placeholder="initiatorId" style={{ width: 140 }} allowClear />
          </Form.Item>
          <Form.Item name="workId" label="作品 ID">
            <Input placeholder="workId" style={{ width: 140 }} allowClear />
          </Form.Item>
          <Form.Item name="userId" label="用户 ID">
            <Input placeholder="userId" style={{ width: 140 }} allowClear />
          </Form.Item>
          <Form.Item name="dateRange" label="创建时间">
            <RangePicker style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={applyFilters} data-testid="restore-jobs-search">
                查询
              </Button>
              <Button onClick={resetFilters} data-testid="restore-jobs-reset">
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <Table<RestoreJobItem>
        rowKey="id"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isLoading}
        scroll={{ x: 1500 }}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        data-testid="restore-jobs-table"
      />

      <InitiateRestoreJobModal
        open={initiateOpen}
        onClose={() => setInitiateOpen(false)}
        onSuccess={invalidate}
      />
      <ApproveRestoreJobModal
        open={approveId != null}
        jobId={approveId}
        onClose={() => setApproveId(null)}
        onSuccess={invalidate}
      />
      <RejectRestoreJobModal
        open={rejectId != null}
        jobId={rejectId}
        onClose={() => setRejectId(null)}
        onSuccess={invalidate}
      />
      <ExecuteRestoreJobModal
        open={executeId != null}
        jobId={executeId}
        onClose={() => setExecuteId(null)}
        onSuccess={invalidate}
      />
      <RestoreJobDetailDrawer
        open={detailId != null}
        jobId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2: LifecycleJobsTab
// ---------------------------------------------------------------------------

interface LifecycleFilterFormValues {
  jobType?: LifecycleJobType | 'all';
  status?: LifecycleJobStatus;
  workId?: string;
  createdBy?: string;
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
}

function LifecycleJobsTab() {
  const [filterForm] = Form.useForm<LifecycleFilterFormValues>();
  const [filter, setFilter] = useState<ListLifecycleJobsFilter>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const query = useQuery({
    queryKey: ['lifecycle-jobs', filter, page, pageSize],
    queryFn: () => listLifecycleJobs({ ...filter, page, pageSize }),
  });

  function applyFilters() {
    const v = filterForm.getFieldsValue();
    const next: ListLifecycleJobsFilter = {};
    if (v.jobType) next.jobType = v.jobType;
    if (v.status) next.status = v.status;
    if (v.workId?.trim()) next.workId = v.workId.trim();
    if (v.createdBy?.trim()) next.createdBy = v.createdBy.trim();
    if (v.dateRange?.[0]) next.fromDate = v.dateRange[0].toISOString();
    if (v.dateRange?.[1]) next.toDate = v.dateRange[1].toISOString();
    setFilter(next);
    setPage(1);
  }

  function resetFilters() {
    filterForm.resetFields();
    setFilter({});
    setPage(1);
  }

  const columns: ColumnsType<LifecycleJobItem> = [
    {
      title: '任务 ID',
      dataIndex: 'jobId',
      key: 'jobId',
      width: 160,
      render: (id: string) => (
        <Tooltip title={id}>
          <Text ellipsis style={{ maxWidth: 140, display: 'block' }}>
            {id}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: '类型',
      dataIndex: 'jobType',
      key: 'jobType',
      width: 130,
      render: (t: LifecycleJobType) => (
        <Tag color={lifecycleJobTypeColor(t)}>{lifecycleJobTypeLabel(t)}</Tag>
      ),
    },
    {
      title: '目标作品 ID',
      dataIndex: 'targetWorkId',
      key: 'targetWorkId',
      width: 140,
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (s: LifecycleJobStatus) => (
        <Tag color={lifecycleStatusColor(s)}>{lifecycleStatusLabel(s)}</Tag>
      ),
    },
    {
      title: '依据 URL',
      dataIndex: 'evidenceUrl',
      key: 'evidenceUrl',
      width: 160,
      render: (url: string | null) => {
        if (!url) return <Text type="secondary">—</Text>;
        return (
          <Tooltip title={url}>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1677ff' }}
              data-testid={`evidence-url-link-${url.slice(-8)}`}
            >
              <Text ellipsis style={{ maxWidth: 140, display: 'inline-block', color: '#1677ff' }}>
                {url}
              </Text>
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: '错误信息',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      width: 160,
      render: (msg: string | null) =>
        msg ? (
          <Tooltip title={msg}>
            <Text type="danger" ellipsis style={{ maxWidth: 140, display: 'block' }}>
              {msg}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: '创建人',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 130,
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: '执行时间',
      dataIndex: 'executedAt',
      key: 'executedAt',
      width: 150,
      render: (v: string | null) => (v ? formatDateTime(v) : '—'),
    },
  ];

  return (
    <div data-testid="lifecycle-jobs-tab">
      <Form
        form={filterForm}
        layout="inline"
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
        data-testid="lifecycle-jobs-filter-form"
      >
        <Form.Item name="jobType" label="类型">
          <Select
            placeholder="全部"
            allowClear
            style={{ width: 140 }}
            options={LIFECYCLE_JOB_TYPE_OPTIONS}
          />
        </Form.Item>
        <Form.Item name="status" label="状态">
          <Select
            placeholder="全部"
            allowClear
            style={{ width: 100 }}
            options={[
              { value: 'pending', label: '待处理' },
              { value: 'running', label: '处理中' },
              { value: 'succeeded', label: '已完成' },
              { value: 'failed', label: '失败' },
            ]}
          />
        </Form.Item>
        <Form.Item name="workId" label="作品 ID">
          <Input placeholder="workId" style={{ width: 140 }} allowClear />
        </Form.Item>
        <Form.Item name="createdBy" label="创建人 ID">
          <Input placeholder="createdBy" style={{ width: 140 }} allowClear />
        </Form.Item>
        <Form.Item name="dateRange" label="创建时间">
          <RangePicker style={{ width: 240 }} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" onClick={applyFilters} data-testid="lifecycle-jobs-search">
              查询
            </Button>
            <Button onClick={resetFilters} data-testid="lifecycle-jobs-reset">
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table<LifecycleJobItem>
        rowKey="jobId"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isLoading}
        scroll={{ x: 1400 }}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        data-testid="lifecycle-jobs-table"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3: ForcePurgeTab — Modals
// ---------------------------------------------------------------------------

interface ForcePurgeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function ForcePurgeModal({ open, onClose, onSuccess }: ForcePurgeModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      const ids = (v.targetWorkIds as string)
        .split('\n')
        .map((s: string) => s.trim())
        .filter(Boolean);
      return triggerForcePurge({ targetWorkIds: ids, reason: v.reason });
    },
    onSuccess: (result) => {
      message.success(`兜底强制清理已发起，共 ${result.jobIds.length} 个 job`);
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '发起强制清理失败');
    },
  });

  return (
    <Modal
      title="兜底批量强制清理"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => {
        form.validateFields().then(() => mutation.mutate()).catch(() => undefined);
      }}
      confirmLoading={mutation.isPending}
      okText="确认执行"
      okButtonProps={{ danger: true }}
      data-testid="force-purge-modal"
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="使用限制"
        description="仅限 work 已过 30 天 + 在回收期 (lifecycle=recycling AND recycle_expire_at < now)。后端会逐个校验。"
        data-testid="force-purge-warning-alert"
      />
      <Form form={form} layout="vertical">
        <Form.Item
          name="targetWorkIds"
          label="目标作品 ID（每行一个，1-100 个）"
          rules={[
            { required: true, message: '请输入作品 ID' },
            {
              validator: (_, value: string) => {
                if (!value) return Promise.resolve();
                const ids = value.split('\n').map((s) => s.trim()).filter(Boolean);
                if (ids.length < 1 || ids.length > 100) {
                  return Promise.reject(new Error('作品 ID 数量需在 1-100 之间'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <TextArea
            rows={6}
            placeholder="每行一个 workId，最多 100 个"
            data-testid="force-purge-work-ids"
          />
        </Form.Item>
        <Form.Item
          name="reason"
          label="清理原因"
          rules={[
            { required: true, message: '请输入清理原因' },
            { min: 5, message: '清理原因不得少于 5 个字符' },
            { max: 200, message: '清理原因不得超过 200 个字符' },
          ]}
        >
          <TextArea rows={3} placeholder="清理原因（5-200 字符）" showCount maxLength={200} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

interface JudicialPurgeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function JudicialPurgeModal({ open, onClose, onSuccess }: JudicialPurgeModalProps) {
  const [form] = Form.useForm();

  const mutation = useMutation({
    mutationFn: () => {
      const v = form.getFieldsValue();
      const ids = (v.targetWorkIds as string)
        .split('\n')
        .map((s: string) => s.trim())
        .filter(Boolean);
      return triggerJudicialPurge({
        targetWorkIds: ids,
        evidenceUrl: v.evidenceUrl,
        reason: v.reason,
        requestType: v.requestType,
      });
    },
    onSuccess: (result) => {
      message.success(`司法清理已发起，共 ${result.jobIds.length} 个 job`);
      form.resetFields();
      onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error(msg ?? '发起司法清理失败');
    },
  });

  return (
    <Modal
      title="司法批量清理"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={() => {
        form.validateFields().then(() => mutation.mutate()).catch(() => undefined);
      }}
      confirmLoading={mutation.isPending}
      okText="确认执行"
      okButtonProps={{ danger: true }}
      data-testid="judicial-purge-modal"
    >
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        message="司法/合规请求专用"
        description="要求三人审批（含法务）+ 上传依据 URL。错误操作将面临法律责任。"
        data-testid="judicial-purge-error-alert"
      />
      <Form form={form} layout="vertical">
        <Form.Item
          name="targetWorkIds"
          label="目标作品 ID（每行一个，1-100 个）"
          rules={[
            { required: true, message: '请输入作品 ID' },
            {
              validator: (_, value: string) => {
                if (!value) return Promise.resolve();
                const ids = value.split('\n').map((s) => s.trim()).filter(Boolean);
                if (ids.length < 1 || ids.length > 100) {
                  return Promise.reject(new Error('作品 ID 数量需在 1-100 之间'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <TextArea
            rows={6}
            placeholder="每行一个 workId，最多 100 个"
            data-testid="judicial-purge-work-ids"
          />
        </Form.Item>
        <Form.Item
          name="evidenceUrl"
          label="依据 URL（必须 https:// 开头）"
          rules={[
            { required: true, message: '请输入依据 URL' },
            {
              pattern: /^https:\/\/.+/,
              message: '依据 URL 必须以 https:// 开头',
            },
          ]}
        >
          <Input
            placeholder="https://..."
            data-testid="judicial-purge-evidence-url"
          />
        </Form.Item>
        <Form.Item
          name="requestType"
          label="请求类型"
          rules={[{ required: true, message: '请选择请求类型' }]}
        >
          <Select
            placeholder="请选择"
            options={[
              { value: 'court_order', label: '法院命令 (court_order)' },
              { value: 'police_request', label: '警方请求 (police_request)' },
              { value: 'compliance', label: '合规要求 (compliance)' },
            ]}
            data-testid="judicial-purge-request-type"
          />
        </Form.Item>
        <Form.Item
          name="reason"
          label="清理原因"
          rules={[
            { required: true, message: '请输入清理原因' },
            { min: 5, message: '清理原因不得少于 5 个字符' },
            { max: 200, message: '清理原因不得超过 200 个字符' },
          ]}
        >
          <TextArea rows={3} placeholder="清理原因（5-200 字符）" showCount maxLength={200} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Tab 3: ForcePurgeTab
// ---------------------------------------------------------------------------

function ForcePurgeTab() {
  const [forcePurgeOpen, setForcePurgeOpen] = useState(false);
  const [judicialPurgeOpen, setJudicialPurgeOpen] = useState(false);

  return (
    <div data-testid="force-purge-tab">
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 24 }}
        message="超管操作警告"
        description="本 Tab 操作不可恢复；仅 super_admin（USER_FREEZE 权限）+ super_admin + legal role 可见。错误操作将面临法律责任。"
        data-testid="force-purge-superadmin-alert"
      />

      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* 兜底强制清理 Card */}
        <PermissionGate point={PERMISSIONS.USER_FREEZE}>
          <Card
            title="兜底强制清理"
            data-testid="force-purge-card"
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              适用场景：work 已过 30 天 + 在回收期（lifecycle=recycling AND recycle_expire_at &lt; now）的兜底场景。需双人审批。
            </Text>
            <Button
              danger
              type="primary"
              onClick={() => setForcePurgeOpen(true)}
              data-testid="force-purge-btn"
            >
              兜底批量清理
            </Button>
          </Card>
        </PermissionGate>

        {/* 司法清理 Card */}
        <PermissionGate point={PERMISSIONS.LIFECYCLE_FORCE_PURGE_JUDICIAL}>
          <Card
            title="司法/合规清理"
            data-testid="judicial-purge-card"
          >
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              适用场景：司法/合规请求（法院命令 / 警方请求 / 合规要求）。需三人审批（含法务）+ 上传依据 URL。
            </Text>
            <Button
              danger
              type="primary"
              onClick={() => setJudicialPurgeOpen(true)}
              data-testid="judicial-purge-btn"
            >
              司法批量清理
            </Button>
          </Card>
        </PermissionGate>
      </Space>

      <ForcePurgeModal
        open={forcePurgeOpen}
        onClose={() => setForcePurgeOpen(false)}
        onSuccess={() => { /* force purge doesn't affect restore-jobs list */ }}
      />
      <JudicialPurgeModal
        open={judicialPurgeOpen}
        onClose={() => setJudicialPurgeOpen(false)}
        onSuccess={() => { /* judicial purge doesn't affect restore-jobs list */ }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page inner
// ---------------------------------------------------------------------------

function LifecyclePageInner() {
  const [activeTab, setActiveTab] = useState('restore-jobs');

  const tabItems = [
    {
      key: 'restore-jobs',
      label: '客服代恢复',
      children: <RestoreJobsTab />,
    },
    {
      key: 'lifecycle-jobs',
      label: '清理监控',
      children: <LifecycleJobsTab />,
    },
    {
      key: 'force-purge',
      label: '强制清理',
      children: <ForcePurgeTab />,
    },
  ];

  return (
    <Card
      title={
        <span>
          删除回收 / A6
          <Text type="secondary" style={{ fontSize: 13, marginLeft: 12, fontWeight: 400 }}>
            客服代恢复 + 普通清理监控 + 强制清理（严格限定）
          </Text>
        </span>
      }
      data-testid="lifecycle-page"
    >
      {/* 严正声明 Banner（必显） */}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 20 }}
        message="严正声明"
        description="禁止对 active 用户作品执行 ad-hoc 强制清理。强制清理仅限：① 已过 30 天 + 已在回收期的兜底场景（双人）② 司法/合规请求（三人 + 上传依据 URL）。违规操作将触发审计告警。"
        data-testid="a6-policy-banner"
      />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        data-testid="lifecycle-tabs"
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Export: page-level PermissionGate USER_READ
// ---------------------------------------------------------------------------

export default function LifecyclePage() {
  return (
    <PermissionGate
      point={PERMISSIONS.USER_READ}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="您没有访问删除回收管理的权限，请联系管理员申请 user:read 权限。"
        />
      }
    >
      <LifecyclePageInner />
    </PermissionGate>
  );
}
